import type { Account } from './account.ts';
import type { ConstValues, WithRequiredNonNullable } from './helpers.ts';
import type { Provider } from './provider.ts';
import type { Audience } from './targeting/audience.ts';

export const segmentExpiredText = (fromDeal: boolean) =>
  fromDeal
    ? 'Audience associated with a given deal has expired segment.'
    : 'Segment data is expired and cannot be used for audience creation.';

export const segmentSoonWillBeExpired = (fromDeal: boolean) =>
  fromDeal
    ? 'Audience associated with a given deal has segments that will expire in next 10 days.'
    : 'Segment is going to expire within the next 10 days.';

// calculated status
export const expiringSoonStatus = 'expiring_soon';
export const segmentStatus = {
  AWAITING_DATA: 'awaiting_data',
  ERROR: 'error',
  EXPIRED: 'expired',
  PUBLISHING: 'publishing',
  READY: 'ready',
  EXPIRING_SOON: expiringSoonStatus,
} as const;
export type SegmentStatus = ConstValues<typeof segmentStatus>;

export interface SegmentAudiences {
  audience: Audience;
  audience_id: string;
  id: string;
  segment: Segment;
  segment_id: string;
}

type SegmentDetailedInfo = {
  provided: number;
  matched: number;
};

type SegmentKeys =
  | 'choreograph'
  | 'cookie'
  | 'idl'
  | 'liveintent'
  | 'device'
  | 'merkle'
  | 'epsilon'
  | 'analyticsiq'
  | 'iqvia'
  | 'email'
  | 'phone'
  | 'rtbhouse'
  | 'klover'
  | 'horizon'
  | 'healthlink'
  | 'ip'
  | 'contanuity'
  | 'id5'
  | 'bombora'
  | 'fantix'
  | 'catalist'
  | 'azerion'
  | 'growthcode'
  | 'wrango';

export type SegmentDetailedCounts = {
  [Key in SegmentKeys]?: SegmentDetailedInfo;
};
export interface CreateSegment {
  account: Pick<Account, 'id' | 'name'>;
  category?: string | null;
  created_date?: string;
  expiration_date?: string;
  full_name?: string | null;
  id: string;
  name: string;
  provider?: Pick<Provider, 'id' | 'name'> | null;
  price_cpm?: number | null;
  price_pom?: number | null;
  segment_category?: string | null;
  segment_sub_category?: string | null;
  sellable: boolean;
  segment_type?: string | null;
  reach?: number | null;
  status?: string | null;
  sub_category?: string | null;
  sub_type?: string | null;
  type?: string | null;
  updated_date?: string;
  parent_segment?: string | null;
}

export interface Segment extends WithRequiredNonNullable<CreateSegment, 'provider'> {
  account_id: string;
  countries?: Array<{ country: { code: string; name: string }; country_code: string }> | null;
  description: string;
  external_id?: string | null;
  full_name?: string | null;
  is_archived?: boolean | null;
  is_leaf?: boolean | null;
  is_nav_only?: boolean | null;
  propensity?: string | null;
  provider_id?: string | null;
  regions?: Array<{ region: { code: string; name: string }; region_code: string }> | null;
  segment_audiences: SegmentAudiences[];
  sharing_type?: string | null;
  source_matched_count?: bigint;
  source_provided_count?: bigint;
  source_detailed_counts?: SegmentDetailedCounts | null;
}

export const renameSegmentFormField = 'new_name';

export type ListSegment = Pick<
  Segment,
  | 'id'
  | 'name'
  | 'reach'
  | 'status'
  | 'created_date'
  | 'updated_date'
  | 'expiration_date'
  | 'account'
  | 'provider'
  | 'price_pom'
  | 'price_cpm'
  | 'segment_category'
  | 'segment_sub_category'
  | 'description'
  | 'sellable'
  | 'segment_type'
  | 'regions'
  | 'countries'
>;

export const segmentRowActionType = {
  DELETE: 'ARCHIVE',
  RENAME: 'RENAME',
  CREATE_LOOKALIKE: 'CREATE_LOOKALIKE',
} as const;
export type SegmentRowActionType = ConstValues<typeof segmentRowActionType>;

export const uploadSegmentFormFields = {
  DATA_TYPE: 'dataType',
  FILE: 'file',
  NAME: 'name',
} as const;
export type UploadSegmentFormFields = ConstValues<typeof uploadSegmentFormFields>;

export const defaultSegmentCategory = 'direct';
export const defaultSegmentSubCategory = 'uploaded';
export const defaultSegmentProvider = 'uploaded';

export const DATE_FORMAT = 'd MMM y';

export const hllMode = {
  NO_SEGMENT_INTERSECTION: 'no-segment-intersection',
  INTERSECT_NINETY_DAY_SEGMENT: 'intersect-ninety-day-segment',
} as const;

export const lalSegmentStatus = {
  ERROR: 'error',
  EXPIRED: 'expired',
  PROCESSING: 'processing',
  READY: 'ready',
} as const;

export type LookalikeSegmentSeedAggregate = {
  id: string;
  lookalike_definition: LalSegment;
  lookalike_definition_id: string;
  segment: Segment;
  segment_id: string;
};

export type LookalikeSeedSource = {
  __typename?: 'lookalike_seed_source';
  id: string;
  lookalike_definition: LalSegment;
  lookalike_definition_id: string;
  segment: Segment;
  segment_id: string;
};

export type LookalikeSeedSourceApi = {
  id: string;
  lookalike_definition_id: string;
  segment: Pick<Segment, 'id' | 'name' | 'reach' | 'status' | 'expiration_date' | 'created_date' | 'updated_date'>;
  segment_id: string;
};

export const lookalikeSegmentSimilarityLevel = {
  BALANCED: 'Balanced',
  HIGH: 'High',
  SCALED: 'Scaled',
} as const;
export type LookalikeSegmentSimilarityLevel = ConstValues<typeof lookalikeSegmentSimilarityLevel>;

export interface LalSegmentBase {
  id: string;
  lookalike_name: string;
  status?: string | null;
  is_archived?: boolean;
  created_date?: string;
  updated_date?: string;
  auc_score?: number | null;
  account: Pick<Account, 'id' | 'name'>;
  provider: Pick<Provider, 'id' | 'name'>;
}

export interface LalSegment extends LalSegmentBase {
  derived_expiry_date?: string | null;
  lookalike_segment_seed_aggregate?: LookalikeSegmentSeedAggregate | null;
  derived_balanced_reach?: number | null;
  lookalike_seeds_aggregate_name?: string | null;
  seeds_estimated_reach?: number | null;
}

export type LookalikeSegmentSimilarity = {
  id: string;
  lookalike_definition: LalSegment;
  lookalike_definition_id: string;
  segment: Segment;
  segment_id: string;
  similarity_level: string;
  similarity_score_threshold: number;
};

export type LookalikeSegmentSimilarityApi = {
  id: string;
  segment: Pick<Segment, 'id' | 'name' | 'reach'>;
  similarity_level: string;
};

export type ListLalSegment = Pick<
  LalSegment,
  | 'id'
  | 'lookalike_name'
  | 'auc_score'
  | 'derived_balanced_reach'
  | 'seeds_estimated_reach'
  | 'lookalike_seeds_aggregate_name'
  | 'status'
  | 'created_date'
  | 'updated_date'
  | 'account'
  | 'provider'
  | 'derived_expiry_date'
> & {
  lookalike_segment_seed_aggregate?: {
    segment: Pick<Segment, 'id' | 'name' | 'status' | 'reach'>;
  } | null;
  lookalike_segment_similarities?: LookalikeSegmentSimilarityApi[] | null;
  error?: { message: string } | null;
};

export const lalSegmentRowActionType = {
  DELETE: 'ARCHIVE',
} as const;
export type LalSegmentRowActionType = ConstValues<typeof lalSegmentRowActionType>;

export interface LalSegmentApi
  extends Omit<LalSegment, 'lookalike_segment_similarities' | 'lookalike_segment_seed_aggregate'> {
  error?: { message: string } | null;
  lookalike_segment_similarities?: LookalikeSegmentSimilarityApi[] | null;
  lookalike_segment_seed_aggregate?: {
    segment: Pick<Segment, 'id' | 'name' | 'status' | 'reach'>;
  } | null;
  lookalike_seed_sources?: LookalikeSeedSourceApi[] | null;
}
export const Actions = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  SET_LIST_DATA: 'SET_LIST_DATA',
} as const;
export type Actions = ConstValues<typeof Actions>;

export type LalFormProvider = Partial<Provider> & Segment['provider'];

export type LalSegmentFormState = {
  [LalSegmentFields.ACCOUNT_ID]: string;
  [LalSegmentFields.NAME]: string;
  [LalSegmentFields.SEEDS]: SeedListData[];
  [LalSegmentFields.SEED_NAME]: string;
  [LalSegmentFields.PROVIDER]: LalFormProvider;
  [LalSegmentFields.ESTIMATED_REACH]: number;
};

export const LalSegmentFields = {
  PROVIDER: 'provider',
  ACCOUNT_ID: 'account_id',
  NAME: 'name',
  SEED_NAME: 'seed_name',
  SEEDS: 'seeds',
  ESTIMATED_REACH: 'estimated_reach',
} as const;
export type LalSegmentFields = ConstValues<typeof LalSegmentFields>;

export type SeedListData = Pick<Segment, 'id' | 'name' | 'status' | 'expiration_date'> & {
  reach: number;
  provider?: string;
  account?: string;
  created?: string;
  updated?: string;
  isOnList: boolean;
};

export type SeedListReducerState = {
  listData: SeedListData[];
};

export type CreateLookalikeSegmentInput = {
  definition: {
    account_id: string;
    lookalike_name: string;
    provider_id: string;
    status: string;
  };
  seedSegmentIds: string[];
};

export type UpdateLookalikeSegmentInput = {
  definition: {
    lookalike_name: string;
    lookalike_seeds_aggregate_name: string;
    seeds_estimated_reach: number;
  };
  seedSegmentIds: string[];
};
